/**
 * Format options for relative time display
 */
export interface FormatRelativeTimeOptions {
  /** Use short format like "3d" vs "3 days" (default: true) */
  short?: boolean;
  /** Include "ago" suffix (default: true for short, varies for long) */
  includeSuffix?: boolean;
}

/**
 * Formats a date as a relative time string (e.g., "3d ago", "2w ago")
 * Consolidated from multiple implementations across the codebase.
 */
export function formatRelativeTime(
  date: Date | string | undefined | null,
  options: FormatRelativeTimeOptions = {}
): string {
  if (!date) return '';

  const { short = true, includeSuffix = true } = options;
  const d = typeof date === 'string' ? new Date(date) : date;
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffMins = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  const diffWeeks = Math.floor(diffDays / 7);
  const diffMonths = Math.floor(diffDays / 30);
  const diffYears = Math.floor(diffDays / 365);

  const suffix = includeSuffix ? ' ago' : '';

  if (short) {
    if (diffMins < 1) return 'now';
    if (diffMins < 60) return `${diffMins}m${suffix}`;
    if (diffHours < 24) return `${diffHours}h${suffix}`;
    if (diffDays === 0) return 'today';
    if (diffDays === 1) return `1d${suffix}`;
    if (diffDays < 7) return `${diffDays}d${suffix}`;
    if (diffWeeks < 4) return `${diffWeeks}w${suffix}`;
    if (diffMonths < 12) return `${diffMonths}mo${suffix}`;
    return `${diffYears}y${suffix}`;
  }

  // Long format
  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''}${suffix}`;
  if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''}${suffix}`;
  if (diffDays === 1) return `1 day${suffix}`;
  if (diffDays < 7) return `${diffDays} days${suffix}`;
  if (diffWeeks < 4) return `${diffWeeks} week${diffWeeks > 1 ? 's' : ''}${suffix}`;
  if (diffMonths < 12) return `${diffMonths} month${diffMonths > 1 ? 's' : ''}${suffix}`;
  return `${diffYears} year${diffYears > 1 ? 's' : ''}${suffix}`;
}

export function formatFutureTime(
  date: Date | string | undefined | null,
  options: FormatRelativeTimeOptions = {}
): string {
  if (!date) return '';

  const { short = true } = options;
  const d = typeof date === 'string' ? new Date(date) : date;
  const now = new Date();
  const diffMs = d.getTime() - now.getTime();

  // If date is in the past, return expired indicator
  if (diffMs <= 0) return 'expired';

  const diffMins = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  const diffWeeks = Math.floor(diffDays / 7);
  const diffMonths = Math.floor(diffDays / 30);

  if (short) {
    if (diffMins < 60) return `in ${diffMins}m`;
    if (diffHours < 24) return `in ${diffHours}h`;
    if (diffDays < 1) return 'today';
    if (diffDays === 1) return 'in 1d';
    if (diffDays < 7) return `in ${diffDays}d`;
    if (diffWeeks < 4) return `in ${diffWeeks}w`;
    return `in ${diffMonths}mo`;
  }

  // Long format
  if (diffMins < 60) return `in ${diffMins} minute${diffMins > 1 ? 's' : ''}`;
  if (diffHours < 24) return `in ${diffHours} hour${diffHours > 1 ? 's' : ''}`;
  if (diffDays === 1) return 'in 1 day';
  if (diffDays < 7) return `in ${diffDays} days`;
  if (diffWeeks < 4) return `in ${diffWeeks} week${diffWeeks > 1 ? 's' : ''}`;
  return `in ${diffMonths} month${diffMonths > 1 ? 's' : ''}`;
}

/**
 * Formats a date as a short absolute date string (e.g., "Jan 5, 2024")
 */
export function formatDate(date: Date | string | undefined | null): string {
  if (!date) return '';
  const d = typeof date === 'string' ? new Date(date) : date;
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(d);
}

/**
 * Formats a date as an absolute date+time string (e.g., "Jan 5, 2024, 2:30 PM")
 */
export function formatDateTime(date: Date | string | undefined | null): string {
  if (!date) return '';
  const d = typeof date === 'string' ? new Date(date) : date;
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(d);
}

/**
 * Formats a date as a time-only string (e.g., "2:30 PM")
 */
export function formatTime(date: Date | string | undefined | null): string {
  if (!date) return '';
  const d = typeof date === 'string' ? new Date(date) : date;
  return new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    minute: '2-digit',
  }).format(d);
}

/**
 * Formats a token count with K/M suffixes for readability
 */
export function formatTokens(value: number): string {
  if (value >= 1_000_000) {
    return `${(value / 1_000_000).toFixed(2)}M`;
  }
  if (value >= 1_000) {
    return `${(value / 1_000).toFixed(1)}K`;
  }
  return value.toLocaleString();
}

/**
 * Formats microdollars as a dollar string (e.g., "$1.23")
 */
export function formatCost(microdollars: number): string {
  const dollars = microdollars / 1_000_000;
  if (dollars < 0.01 && dollars > 0) {
    return '<$0.01';
  }
  return `$${dollars.toFixed(2)}`;
}

/**
 * Formats a byte rate as a structured { value, suffix } pair (e.g., { value: "1.2", suffix: "MB/s" })
 */
export function formatBytesPerSec(bytes: number): { value: string; suffix: string } {
  if (bytes < 1024) return { value: `${Math.round(bytes)}`, suffix: 'B/s' };
  if (bytes < 1024 * 1024) return { value: `${(bytes / 1024).toFixed(1)}`, suffix: 'KB/s' };
  return { value: `${(bytes / (1024 * 1024)).toFixed(1)}`, suffix: 'MB/s' };
}

/**
 * Formats a byte rate as a single compact string (e.g., "245 KB/s")
 */
export function formatBytesPerSecCompact(bytes: number): string {
  const { value, suffix } = formatBytesPerSec(bytes);
  return `${value} ${suffix}`;
}

/**
 * Formats hours as a compact uptime string (e.g., "2h 18m", "3d 12h")
 */
export function formatUptime(hours: number): string {
  if (hours < 1) return `${Math.round(hours * 60)}m`;
  if (hours < 24) return `${Math.floor(hours)}h ${Math.round((hours % 1) * 60)}m`;
  const days = Math.floor(hours / 24);
  const rem = Math.round(hours % 24);
  return `${days}d ${rem}h`;
}

/**
 * Formats a load average value as { value, suffix } for sparkline display.
 */
export function formatLoadAverage(load: number): { value: string; suffix: string } {
  return { value: load.toFixed(2), suffix: '' };
}

/**
 * Formats bytes as a human-readable string (e.g., "1.5 GB")
 */
export function formatBytes(bytes: number): string {
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB'];
  if (bytes === 0) return '0 Bytes';
  // S3-backed storage reports ~8 EB (max int64), treat as unlimited
  if (bytes > 1e18) return 'Unlimited';
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return `${(bytes / Math.pow(1024, i)).toFixed(2)} ${sizes[i]}`;
}

export function formatCurrency(amount: number, currency: string): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency.toUpperCase(),
  }).format(amount);
}

export function formatTimestamp(timestamp: number): string {
  const ms = timestamp > 10_000_000_000 ? timestamp : timestamp * 1000;
  return formatDate(new Date(ms));
}

export function formatCardBrand(brand: string): string {
  const brands: Record<string, string> = {
    visa: 'Visa',
    mastercard: 'Mastercard',
    amex: 'American Express',
    discover: 'Discover',
  };
  return brands[brand.toLowerCase()] ?? brand;
}

/**
 * Formats milliseconds as Xh Ym.
 * e.g. 55_800_000 -> "15h 30m", 2_700_000 -> "0h 45m"
 */
export function formatWorkHours(ms: number): string {
  const totalMinutes = Math.round(ms / 60_000);
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  return `${h}h ${m}m`;
}

export function getStartOfMonth(): Date {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), 1);
}
